import type {
  GenerateRequest,
  GenerateResponse,
  AutoGenerateRequest,
  AutoGenerateResponse,
} from '../types';
import type {
  StartScanRequest,
  StartScanResponse,
  ScanStatusResponse,
} from '../types/scanner';
import { getCsrfToken } from './authApi';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Callback for handling 401 responses
 * Set by AuthContext to clear auth state and redirect to login
 */
let onUnauthorized: (() => void) | null = null;

/**
 * Register a callback to handle 401 Unauthorized responses
 * This allows the API layer to trigger auth state clearing without circular dependencies
 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * Clear the unauthorized handler
 */
export function clearUnauthorizedHandler(): void {
  onUnauthorized = null;
}

/**
 * Handle API response with 401 interception
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Handle 401 Unauthorized - session expired or invalid
    if (response.status === 401) {
      if (onUnauthorized) {
        onUnauthorized();
      }
      throw new ApiError(response.status, 'Session expired. Please log in again.');
    }

    const body = await response.json().catch(() => ({ error: { message: 'An error occurred' } }));
    // Error response format: { success: false, error: { message: "...", code: "..." } }
    const errorData = body.error || body;
    throw new ApiError(response.status, errorData.message || 'An error occurred');
  }
  return response.json();
}

/**
 * Get headers for API requests
 * Includes CSRF token for state-changing methods (POST, PATCH, DELETE)
 */
function getHeaders(method: string = 'GET'): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Include CSRF token for state-changing requests
  const csrfMethods = ['POST', 'PATCH', 'PUT', 'DELETE'];
  if (csrfMethods.includes(method.toUpperCase())) {
    const token = getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  return headers;
}

/**
 * Build request options with credentials and optional signal
 */
function buildRequestOptions(
  method: string,
  body?: unknown,
  signal?: AbortSignal,
  noCache?: boolean
): RequestInit {
  const options: RequestInit = {
    method,
    headers: getHeaders(method),
    credentials: 'include', // Always include cookies for session management
  };

  if (body !== undefined) {
    options.body = JSON.stringify(body);
  }

  if (signal) {
    options.signal = signal;
  }

  // Prevent browser caching for polling endpoints
  if (noCache) {
    options.cache = 'no-store';
  }

  return options;
}

/**
 * Returns true if the given error was caused by an AbortController.abort() call.
 * Consumers should use this to silently ignore cancelled requests instead of
 * surfacing an error to the user.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export const api = {
  async getCountries(signal?: AbortSignal): Promise<{ code: string; name: string }[]> {
    const response = await fetch(
      `${API_BASE}/countries`,
      buildRequestOptions('GET', undefined, signal),
    );
    return handleResponse(response);
  },

  async getCities(countryCode: string, signal?: AbortSignal): Promise<{ name: string; region?: string }[]> {
    const response = await fetch(
      `${API_BASE}/cities/${countryCode}`,
      buildRequestOptions('GET', undefined, signal),
    );
    return handleResponse(response);
  },

  async autoGenerate(data: AutoGenerateRequest, signal?: AbortSignal): Promise<AutoGenerateResponse> {
    const response = await fetch(
      `${API_BASE}/auto-generate`,
      buildRequestOptions('POST', data, signal),
    );
    return handleResponse(response);
  },

  async startGeneration(
    data: GenerateRequest,
    idempotencyKey?: string,
    signal?: AbortSignal,
  ): Promise<{ sessionId: string; status: string }> {
    const options = buildRequestOptions('POST', data, signal);

    // Attach an idempotency key so retried requests return the same session
    // instead of creating a duplicate generation. If none is provided by the
    // caller, generate one automatically using crypto.randomUUID().
    const key = idempotencyKey ?? crypto.randomUUID();
    (options.headers as Record<string, string>)['Idempotency-Key'] = key;

    const response = await fetch(`${API_BASE}/generate`, options);
    const result = await handleResponse<{ sessionId: string; status: string } | GenerateResponse>(response);
    if ('files' in result) {
      return { sessionId: (result as any).sessionId, status: 'complete' };
    }
    return result as { sessionId: string; status: string };
  },

  async getGenerationStatus(
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<{ status: string; progress?: number; message?: string; error?: string }> {
    const response = await fetch(
      `${API_BASE}/generate/status/${sessionId}`,
      buildRequestOptions('GET', undefined, signal, true), // noCache=true to prevent stale responses
    );
    return handleResponse(response);
  },

  async getSession(sessionId: string, signal?: AbortSignal): Promise<GenerateResponse> {
    const response = await fetch(
      `${API_BASE}/generate/session/${sessionId}`,
      buildRequestOptions('GET', undefined, signal),
    );
    return handleResponse(response);
  },

  async listGenerations(
    params?: { page?: number; limit?: number; signal?: AbortSignal } | AbortSignal,
  ): Promise<{
    sessions: Array<{
      sessionId: string;
      companyName: string;
      status: 'pending' | 'generating' | 'complete' | 'failed';
      progress: number;
      message: string;
      createdAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    // Support both old signature (signal only) and new signature (params object)
    let signal: AbortSignal | undefined;
    let page: number | undefined;
    let limit: number | undefined;

    if (params instanceof AbortSignal) {
      signal = params;
    } else if (params) {
      signal = params.signal;
      page = params.page;
      limit = params.limit;
    }

    const queryParams = new URLSearchParams();
    if (page !== undefined) queryParams.set('page', String(page));
    if (limit !== undefined) queryParams.set('limit', String(limit));
    const queryString = queryParams.toString();
    const url = `${API_BASE}/generate/list${queryString ? `?${queryString}` : ''}`;

    // Use noCache to prevent browser from serving stale data from a previous user's session
    const response = await fetch(url, buildRequestOptions('GET', undefined, signal, true));
    return handleResponse(response);
  },

  async deleteGeneration(sessionId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(
      `${API_BASE}/generate/session/${sessionId}`,
      buildRequestOptions('DELETE', undefined, signal),
    );
    if (!response.ok) {
      if (response.status === 401 && onUnauthorized) {
        onUnauthorized();
      }
      throw new ApiError(response.status, 'Failed to delete generation');
    }
  },

  async cancelGeneration(sessionId: string, signal?: AbortSignal): Promise<void> {
    const response = await fetch(
      `${API_BASE}/generate/cancel/${sessionId}`,
      buildRequestOptions('POST', undefined, signal),
    );
    if (!response.ok) {
      if (response.status === 401 && onUnauthorized) {
        onUnauthorized();
      }
      throw new ApiError(response.status, 'Failed to cancel generation');
    }
  },

  getDownloadUrl(sessionId: string): string {
    return `${API_BASE}/download/${sessionId}`;
  },

  async healthCheck(signal?: AbortSignal): Promise<{ status: string }> {
    const response = await fetch(
      `${API_BASE}/health`,
      buildRequestOptions('GET', undefined, signal),
    );
    return handleResponse(response);
  },

  // Scanner API methods
  async startScan(request: StartScanRequest): Promise<StartScanResponse> {
    const response = await fetch(
      `${API_BASE}/scan`,
      buildRequestOptions('POST', request),
    );
    return handleResponse(response);
  },

  async getScanStatus(scanId: string): Promise<ScanStatusResponse> {
    const response = await fetch(
      `${API_BASE}/scan/${scanId}`,
      buildRequestOptions('GET'),
    );
    return handleResponse(response);
  },

  async startFileScan(file: File, region: string, industry: string): Promise<ScanStatusResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('region', region);
    formData.append('industry', industry);

    const options: RequestInit = {
      method: 'POST',
      credentials: 'include',
      body: formData,
    };

    const response = await fetch(`${API_BASE}/scan/files`, options);
    return handleResponse(response);
  },

  async downloadScanReport(scanId: string, format: 'pdf' | 'json' | 'md' = 'pdf'): Promise<Blob> {
    const response = await fetch(
      `${API_BASE}/scan/${scanId}/report?format=${format}`,
      buildRequestOptions('GET'),
    );
    if (!response.ok) {
      throw new ApiError(response.status, 'Failed to download report');
    }
    return response.blob();
  },

  saveScanReport(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

export { ApiError };
export default api;
